import * as React from "react";
import {RaisedButton} from "../../common/RaisedButton";
import {useState} from "react";
import {prominentButtonStyle, secondaryButtonStyle} from "../../common/buttonStyles";
import {LoginContentAdminApi} from "../../../api/LoginContentAdminApi";
import {IApiResult} from "../../../api/AxiosWealthManagementApi";
import {IToastNotification} from "../../../utils/planUserRemoval";
import {SelectComponentReact} from "../../common/ReactSelectComponent";
import {AdminRegion, CreateRegionRequest} from "../../../model/RegionLoginContent.model";

export const ADMIN_LOGIN_CONTENT_ADD_REGION_HEADING = "Add New Region";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_GEO_TYPE_TITLE = "New geographic region";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_GEO_TYPE_DESC = "Content based on user's country";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_TYPE_TITLE = "Language variant";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_TYPE_DESC = "Translated content for browser language";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_NAME_LABEL = "Region Name";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_CREATE_BTN = "Create Region";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_CREATE_VARIANT_BTN = "Create Variant";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_DEFAULT_LABEL = "Default Region";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_DEFAULT_EXISTS_ERROR = "A default region already exists";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_NAME_REQUIRED_ERROR = "Region name is required";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_PARENT_REQUIRED_ERROR = "Please select a parent region";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_REQUIRED_ERROR = "Please select a browser language";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_CREATED_SUCCESS = "Region created";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_CREATED_ERROR = "Failed to create region";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_INFO_TITLE = "How language detection works";
export const ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_INFO_DESC = "Every browser sends an Accept-Language header (e.g. fr-CA for French Canadian). When a user's country matches the parent region and their browser language matches, they see this variant.";

interface AdminLoginContentAddRegionProps {
    regions: AdminRegion[];
    setShowAddRegion: (show: boolean) => void;
    setActiveRegion: (region: AdminRegion) => void;
    setToast: (toast: IToastNotification) => void;
}

type SelectOption = {id: string | number; name: string};

enum RegionType {
    Geographic = "geo",
    Language = "lang",
}

export const AdminLoginContentAddRegion: React.FunctionComponent<AdminLoginContentAddRegionProps> = (props: AdminLoginContentAddRegionProps) => {
    const SELECT_PARENT = "select_parent";
    const SELECT_LANGUAGE = "select_lang";
    const LANGUAGE_OPTIONS: SelectOption[] = [
        {id: SELECT_LANGUAGE, name: "Select language..."},
        {id: "fr", name: "French (fr)"},
    ];
    const parentRegions = props.regions.filter(r => !r.parentRegionId);
    const [newRegionType, setNewRegionType] = useState<RegionType>(RegionType.Geographic);
    const [newRegionName, setNewRegionName] = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [selectedParentRegionId, setSelectedParentRegionId] = useState<string | number>(SELECT_PARENT);
    const [selectedLanguageCode, setSelectedLanguageCode] = useState<string>(SELECT_LANGUAGE);

    const buildRequest = (): CreateRegionRequest | null => {
        if (isDefault) {
            if (props.regions.some(r => r.isDefault)) {
                props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_DEFAULT_EXISTS_ERROR, type: "error"});
                return null;
            }
            if (!newRegionName.trim()) {
                props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_NAME_REQUIRED_ERROR, type: "error"});
                return null;
            }
            return {regionName: newRegionName.trim(), isDefault};
        }
        if (newRegionType === RegionType.Geographic) {
            if (!newRegionName.trim()) {
                props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_NAME_REQUIRED_ERROR, type: "error"});
                return null;
            }
            return {regionName: newRegionName.trim(), isDefault};
        }
        if (selectedParentRegionId === SELECT_PARENT) {
            props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_PARENT_REQUIRED_ERROR, type: "error"});
            return null;
        }
        if (selectedLanguageCode === SELECT_LANGUAGE) {
            props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_REQUIRED_ERROR, type: "error"});
            return null;
        }
        const parent = parentRegions.find(r => r.id === selectedParentRegionId)!;
        return {
            regionName: `${parent.regionName} (${selectedLanguageCode.toUpperCase()})`,
            parentRegionId: parent.id,
            languageCode: selectedLanguageCode,
            isDefault,
        };
    };

    const createRegionHandler = () => {
        const request = buildRequest();
        if (!request) return;
        LoginContentAdminApi.createRegion(request)
            .then((response: IApiResult<AdminRegion>) => {
                props.setShowAddRegion(false);
                response.data && props.setActiveRegion(response.data);
                props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_CREATED_SUCCESS, type: "success"});
            }).catch(() => {
                props.setToast({message: ADMIN_LOGIN_CONTENT_ADD_REGION_CREATED_ERROR, type: "error"});
            });
    };

    const onDefaultRegionClick = (checked: boolean) => {
        setIsDefault(checked);
        if (checked) {
            setNewRegionType(RegionType.Geographic);
        }
    };

    const renderHeader = () => {
        return <div className="admin-login-content__add-region-header">
            <h3><i className="fa-solid fa-globe" /> {ADMIN_LOGIN_CONTENT_ADD_REGION_HEADING}</h3>
            <label className="admin-login-content__default-region-toggle">
                <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={e => onDefaultRegionClick(e.target.checked)}
                />
                {ADMIN_LOGIN_CONTENT_ADD_REGION_DEFAULT_LABEL}
            </label>
        </div>;
    };

    const renderRegionTypeOption = (value: RegionType, title: string, description: string) => {
        const isActive = newRegionType === value;

        return (
            <label
                key={value}
                className={`admin-login-content__region-type-option${isActive ? " admin-login-content__region-type-option--active" : ""}`}
            >
                <input
                    type="radio"
                    name="regionType"
                    value={value}
                    checked={isActive}
                    onChange={() => setNewRegionType(value)}
                />
                <div>
                    <div className="admin-login-content__region-type-title">{title}</div>
                    <div className="admin-login-content__region-type-desc">{description}</div>
                </div>
            </label>
        );
    };

    const renderGeographicRegion = () => {
        return (
            <div>
                <p className="admin-login-content__form-hint-block">
                    Create a new region to show customized login page content.
                    You can assign countries to it after creation.
                </p>
                <div className="admin-login-content__form-group">
                    <label className="admin-login-content__label" htmlFor="alc-region-name">
                        {ADMIN_LOGIN_CONTENT_ADD_REGION_NAME_LABEL}
                    </label>
                    <input
                        id="alc-region-name"
                        type="text"
                        className="admin-login-content__input"
                        value={newRegionName}
                        onChange={e => setNewRegionName(e.target.value)}
                        placeholder="e.g. Germany, Asia Pacific"
                    />
                </div>
            </div>
        );
    };

    const renderLanguageInfoBox = () => {
        return (
            <div className="admin-login-content__lang-info-box">
                <i className="fa-solid fa-circle-info" />
                <div>
                    <strong>{ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_INFO_TITLE}</strong>
                    <span>{ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_INFO_DESC}</span>
                </div>
            </div>
        );
    };

    const renderParentRegionSelect = () => {
        const parentRegionOptions: SelectOption[] = [
            {id: SELECT_PARENT, name: "Select parent region..."},
            ...parentRegions.filter(r => !r.isDefault).map(r => ({id: r.id, name: r.regionName})),
        ];
        const selectedOption = parentRegionOptions.find(o => o.id === selectedParentRegionId) ?? parentRegionOptions[0];
        return (
            <div className="admin-login-content__form-group">
                <label className="admin-login-content__label" htmlFor="alc-parent-region">
                    Parent Region
                </label>
                <SelectComponentReact
                    id="alc-parent-region"
                    options={parentRegionOptions}
                    value={selectedOption}
                    onChange={(option) => setSelectedParentRegionId((option as SelectOption).id)}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => String(o.id)}
                    submitClicked={false}
                    renderRequired={false}
                    stylesObject={{width: "100%"}}
                />
                <span className="admin-login-content__form-hint">
                    Country routing is inherited from this region.
                </span>
            </div>
        );
    };

    const renderBrowserLanguageSelect = () => {
        const selectedLanguageOption = LANGUAGE_OPTIONS.find(o => o.id === selectedLanguageCode) ?? LANGUAGE_OPTIONS[0];
        return (
            <div className="admin-login-content__form-group">
                <label className="admin-login-content__label" htmlFor="alc-lang-code">
                    Browser Language
                </label>
                <SelectComponentReact
                    id="alc-lang-code"
                    options={LANGUAGE_OPTIONS}
                    value={selectedLanguageOption}
                    onChange={(option) => setSelectedLanguageCode(String((option as SelectOption).id))}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => String(o.id)}
                    submitClicked={false}
                    renderRequired={false}
                    stylesObject={{width: "100%"}}
                />
                <span className="admin-login-content__form-hint">
                    Matches the primary language from Accept-Language header.
                </span>
            </div>
        );
    };

    const renderLanguageVariantRegion = () => {
        return (
            <div>
                {renderLanguageInfoBox()}
                <div className="admin-login-content__form-row">
                    {renderParentRegionSelect()}
                    {renderBrowserLanguageSelect()}
                </div>
            </div>
        );
    };

    const renderCreateCancelButton = () => {
        return <div className="admin-login-content__card-edit-actions">
            <RaisedButton
                type="button"
                className="admin-login-content__btn admin-login-content__btn--primary"
                style={prominentButtonStyle}
                primary={true}
                onClick={() => void createRegionHandler()}>
                <i className="fa-solid fa-plus" />
                {` ${newRegionType === RegionType.Language ? ADMIN_LOGIN_CONTENT_ADD_REGION_CREATE_VARIANT_BTN : ADMIN_LOGIN_CONTENT_ADD_REGION_CREATE_BTN}`}
            </RaisedButton>
            <RaisedButton
                type="button"
                className="admin-login-content__btn admin-login-content__btn--secondary"
                style={secondaryButtonStyle}
                primary={false}
                onClick={() => props.setShowAddRegion(false)}>
                Cancel
            </RaisedButton>
        </div>;
    };

    return <div className="admin-login-content__add-region-card" id="add-region-form">
        {renderHeader()}
        <div className="admin-login-content__add-region-body">
            <div className="admin-login-content__region-type-legend">Region type</div>
            <div className={`admin-login-content__region-type-toggle${isDefault ? " default" : ""}`}>
                {renderRegionTypeOption(RegionType.Geographic, ADMIN_LOGIN_CONTENT_ADD_REGION_GEO_TYPE_TITLE,
                    ADMIN_LOGIN_CONTENT_ADD_REGION_GEO_TYPE_DESC)}
                {!isDefault
                    && renderRegionTypeOption(RegionType.Language, ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_TYPE_TITLE,
                    ADMIN_LOGIN_CONTENT_ADD_REGION_LANG_TYPE_DESC)}
            </div>
            {newRegionType === RegionType.Geographic && renderGeographicRegion()}
            {newRegionType === RegionType.Language && renderLanguageVariantRegion()}
            {renderCreateCancelButton()}
        </div>
    </div>;
};
